/**
 * O único lugar que estende o dayjs.
 *
 * `dayjs.extend` muta o singleton do pacote, então plugin estendido em qualquer arquivo passa a
 * valer no app inteiro. Isso é o que fazia a versão espalhada "funcionar": `.utc()` num módulo só
 * respondia porque outro módulo, importado antes, tinha estendido o plugin. Mude a ordem dos
 * imports e o mesmo código passa a devolver Invalid Date.
 *
 * Importado por `src/index.tsx` (app) e pelo setup do Vitest (testes), que são os dois pontos de
 * entrada. `scripts/assert-dayjs-config.mjs` barra `dayjs.extend` fora daqui.
 */
import dayjs from 'dayjs';

import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';

import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/pt-br';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(customParseFormat);
dayjs.extend(calendar);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

/**
 * O moment trazia `ll`/`lll` e um `calendar.sameElse` que o dayjs não define igual. Os call sites
 * que dependiam disso (`utils/date.ts`, células de sync e de recibo) continuam pedindo os mesmos
 * tokens, então o formato mora aqui em vez de em cada tela.
 */
const LONG_DATE = {
  formats: {
    L: 'DD/MM/YYYY',
    LL: 'D [de] MMMM [de] YYYY',
    ll: 'D MMM YYYY',
    lll: 'D MMM YYYY HH:mm',
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    LLL: 'D [de] MMMM [de] YYYY HH:mm',
    LLLL: 'dddd, D [de] MMMM [de] YYYY HH:mm',
  },
  calendar: {
    sameElse: 'D MMM YYYY HH:mm',
  },
};

dayjs.updateLocale('pt', LONG_DATE);
dayjs.updateLocale('pt-br', LONG_DATE);
dayjs.updateLocale('es', {
  ...LONG_DATE,
  formats: { ...LONG_DATE.formats, LLL: 'D [de] MMMM [de] YYYY H:mm', LLLL: 'dddd, D [de] MMMM [de] YYYY H:mm' },
});
dayjs.updateLocale('en', {
  ...LONG_DATE,
  formats: {
    ...LONG_DATE.formats,
    L: 'MM/DD/YYYY',
    LL: 'MMMM D, YYYY',
    LT: 'h:mm A',
    LTS: 'h:mm:ss A',
    LLL: 'MMMM D, YYYY h:mm A',
    LLLL: 'dddd, MMMM D, YYYY h:mm A',
  },
});

export default dayjs;
